<script>
import { useMediaQuery } from '@vueuse/core';
import useBasket from '@/stores/Basket';
import useProduct from '@/stores/Product';
import { computed, ref } from 'vue';
import Helper from '@/libraries/Helpers';

export default {
  name: 'ProductItem',
  props: {
    product: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const isLargeScreen = useMediaQuery('(min-width: 1024px)');
    const { getSettings } = useBasket();
    const { updateProduct, removeProduct } = useProduct();

    const amount = ref(props.product.amount);

    const changeAmount = (value, oldValue) => {
      let newValue;

      if (value === 'inc') {
        if (amount.value < props.product.maxAmount) {
          amount.value += 1;
        }
      } else if (value === 'dec') {
        if (amount.value === 1) {
          return false;
        }
        amount.value -= 1;
      } else {
        if (isNaN(value)) {
          newValue = 1;
        } else {
          newValue = parseInt(value);
        }

        if (value === oldValue) {
          return false;
        }

        if (newValue <= 0) {
          amount.value = 1;
        } else {
          if (newValue > props.product.maxAmount) {
            amount.value = props.product.maxAmount;
          } else {
            amount.value = newValue;
          }
        }
      }

      updateProduct(props.product.uID, amount.value);
    };

    return {
      isLargeScreen,
      currency: computed(() => getSettings('currency')),
      amount,
      changeAmount,
      removeProduct,
      Helper
    };
  }
};
</script>

<template>
  <div class="flex justify-between items-center flex-wrap py-10 lg:flex-nowrap">
    <div class="order-1 w-80% lg:w-196">
      <h3 class="text-10 font-normal mb-0 lg:text-6.5">
        {{ product.productName }}
      </h3>

      <div v-if="product.parameters.length">
        <div
          class="text-8.5 font-normal lg:text-6"
          v-for="(parameter, parameterIndex) in product.parameters"
          :key="`pp${parameterIndex}`"
        >
          <template v-if="parameter.name">
            {{ parameter.name }}: {{ parameter.value }}
          </template>
          <template v-else>
            {{ parameter.value }}
          </template>
        </div>
      </div>
    </div>

    <div class="order-2 text-6.5 w-16% text-6.5" v-if="isLargeScreen">
      {{ currency
      }}{{
        Helper.formatPrice(product.discountPriceWithVat || product.priceWithVat)
      }}
    </div>

    <div
      class="order-3 w-76 text-center"
      v-if="
        product.barCode === 'ROOM_ORDER' ||
          product.barCode === 'ROOM_ORDER_EXTRA'
      "
    >
      {{ amount }}
    </div>

    <div
      class="order-3 flex items-center text-14px font-medium uppercase w-76 mt-12 lg:mt-0"
      v-else
    >
      <button
        type="button"
        class="
              flex-shrink-0
              w-16
              h-16
              text-12 text-blue-900
              bg-yellow
              rounded-full
              leading-none
              transition-colors
              duration-300
              hover:bg-yellow-hover
              mr-3
            "
        :disabled="disabled"
        @click="changeAmount('dec')"
      >
        <span class="block -mt-2px">-</span>
      </button>
      <input
        type="number"
        inputmode="numeric"
        class="h-16 form-control text-center font-bold number-appearance-none"
        :disabled="disabled"
        v-model="amount"
        @change="changeAmount($event.target.value)"
      />
      <button
        type="button"
        class="
              flex-shrink-0
              w-16
              h-16
              text-12 text-blue-900
              bg-yellow
              rounded-full
              leading-none
              transition-colors
              duration-300
              hover:bg-yellow-hover
              ml-3
            "
        :disabled="disabled"
        @click="changeAmount('inc')"
      >
        <span class="block -mt-2px">+</span>
      </button>
    </div>

    <div class="order-4 flex-grow text-10 text-right mt-12 lg:text-6.5 lg:mt-0">
      {{ currency
      }}{{
        Helper.formatPrice(
          product.totalDiscountPriceWithVat || product.totalPriceWithVat
        )
      }}
    </div>

    <div class="order-2 w-20% text-right lg:order-5 lg:w-20">
      <button
        type="button"
        class="inline-flex justify-center items-center w-12 h-12 -mr-3 text-gray-900 transition-colors duration-300 hover:text-gray-900-hover focus:outline-none"
        :disabled="disabled"
        @click.prevent="removeProduct(product.uID)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-12 h-12 lg:h-6 lg:w-6"
          fill="#797979"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </div>
</template>
